import { useId, useRef, useState } from "react";

export default function CategorySelect({ categories, value, onChange, loading, disabled }) {
  const listId = useId();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = categories.find((category) => String(category.id) === String(value));
  const options = categories.filter((category) => category.name.toLowerCase().includes(query.trim().toLowerCase()));
  const expanded = open && !disabled && !loading;

  const showOptions = () => {
    setQuery("");
    setActiveIndex(0);
    setOpen(true);
  };
  const select = (category) => {
    onChange(String(category.id));
    setOpen(false);
    setQuery("");
  };
  const move = (index) => {
    setActiveIndex(index);
    document.getElementById(`${listId}-${index}`)?.scrollIntoView?.({ block: "nearest" });
  };

  return (
    <div style={{ position: "relative", marginBottom: 14 }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <label htmlFor={`${listId}-input`} style={{ display: "block", marginBottom: 6, fontSize: 12.5, fontWeight: 700, color: "#334155" }}>Category</label>
      <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
        <input
          ref={inputRef}
          id={`${listId}-input`}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-activedescendant={expanded && options[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          autoComplete="off"
          value={expanded ? query : selected?.name || ""}
          placeholder={loading ? "Loading categories…" : "Select or search category…"}
          disabled={disabled || loading}
          onFocus={showOptions}
          onClick={() => { if (!open) showOptions(); }}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
            onChange("");
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              if (!expanded) showOptions();
              else move(Math.max(0, Math.min(options.length - 1, activeIndex + (event.key === "ArrowDown" ? 1 : -1))));
            } else if (event.key === "Enter" && expanded) {
              event.preventDefault();
              if (options[activeIndex]) select(options[activeIndex]);
            } else if (event.key === "Escape") {
              setOpen(false);
            }
          }}
          style={{ flex: 1, minWidth: 0, padding: "10px 12px", border: 0, background: "transparent", fontSize: 13.5, color: "#334155" }}
        />
        <button type="button" aria-label="Toggle category options" disabled={disabled || loading}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (expanded) setOpen(false);
            else { inputRef.current?.focus(); showOptions(); }
          }}
          style={{ border: 0, background: "transparent", padding: "0 12px", color: "#64748b", cursor: "pointer" }}>
          <span aria-hidden="true">▾</span>
        </button>
      </div>
      {expanded && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1, marginTop: 4, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 6px 20px rgba(15,23,42,.12)", maxHeight: 220, overflowY: "auto" }}>
          <ul id={listId} role="listbox" aria-label="Categories" style={{ listStyle: "none", margin: 0, padding: 4 }}>
            {options.map((category, index) => (
              <li key={category.id} id={`${listId}-${index}`} role="option" aria-selected={String(category.id) === String(value)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(category)}
                onMouseMove={() => setActiveIndex(index)}
                style={{ padding: "9px 10px", borderRadius: 5, fontSize: 13.5, cursor: "pointer", background: index === activeIndex ? "#eff6ff" : "transparent", color: "#334155" }}>
                {category.name}
              </li>
            ))}
          </ul>
          {!options.length && <div role="status" style={{ padding: "12px 14px", fontSize: 13, color: "#64748b" }}>No categories found.</div>}
        </div>
      )}
    </div>
  );
}
